import { useMemo, useState } from "react";
import { where, orderBy, limit as fbLimit, type QueryConstraint } from "firebase/firestore";
import { useTranslations } from "@/contexts/I18nContext";
import { useHouseholdContext } from "@/contexts/HouseholdContext";
import { useFirestoreCollection } from "@/hooks/useFirestore";
import { Card } from "@/components/ui/Card";
import { EmptyState } from "@/components/ui/EmptyState";
import { CookbookMealCard } from "@/components/cards/CookbookMealCard";
import { CookbookRecipeDetailSheet } from "@/components/features/CookbookRecipeDetailSheet";
import type { CookbookMeal } from "@/lib/models";

// Genre + dietary taxonomy mirrors iOS `CookbookGenre` / `CookbookDietaryTag`
// in `Views/Cookbook/CookbookModels.swift`. Web shows whichever genres are
// actually present in the loaded meal set so empty pills don't cluttter the
// scroller. Dietary chips are static — matches iOS row.
const ALL_GENRES = [
  "italian",
  "mexican",
  "chinese",
  "japanese",
  "indian",
  "thai",
  "mediterranean",
  "american_comfort",
  "bbq",
  "vegetarian",
  "healthy",
  "kid_friendly",
  "holiday",
  "breakfast",
] as const;

const DIETARY_TAGS = [
  "vegetarian",
  "vegan",
  "pescatarian",
  "gluten_free",
  "lactose_free",
  "halal",
  "kosher",
  "keto",
] as const;

type Genre = (typeof ALL_GENRES)[number];
type DietaryTag = (typeof DIETARY_TAGS)[number];

const DEFAULT_GENRE: Genre = "italian";

/**
 * Member-facing Cookbook page. Mirrors iOS
 * `Views/Cookbook/CookbookView.swift` shape — horizontal genre pill
 * scroller, dietary chip filter row, responsive 2-column meal grid.
 * Tapping a card opens `CookbookRecipeDetailSheet`.
 *
 * Source of truth: the global `meal_catalog` collection — same path
 * iOS canonical reads. Firestore rules
 * (`firestore.rules` `match /meal_catalog/{mealId}`) gate to
 * `review_status == "approved"` for all auth'd readers, which is the
 * read filter applied here.
 */
export function CookbookPage() {
  const { householdId, loading: ctxLoading } = useHouseholdContext();
  const t = useTranslations();

  const [selectedGenre, setSelectedGenre] = useState<Genre>(DEFAULT_GENRE);
  const [activeDietary, setActiveDietary] = useState<Set<DietaryTag>>(new Set());
  const [detailMeal, setDetailMeal] = useState<CookbookMeal | null>(null);

  // iOS `CookbookRepository.subscribe` always pins one genre via
  // `arrayContains` (`Views/Cookbook/CookbookRepository.swift:35-40`)
  // because Firestore rules + composite-index coverage require that
  // constraint. Web mirrors the same query shape — selecting a genre
  // re-subscribes; dietary filter stays client-side. Without the
  // genre constraint Firestore returns FAILED_PRECONDITION because
  // there's no index for `(review_status, source, popularity_score)`
  // alone.
  const constraints = useMemo<QueryConstraint[]>(
    () => [
      where("review_status", "==", "approved"),
      where("source", "==", "haiku_generated"),
      where("genre", "array-contains", selectedGenre),
      orderBy("popularity_score", "desc"),
      fbLimit(200),
    ],
    [selectedGenre],
  );

  // Explicit `key` is required here — `useFirestoreCollection`'s
  // default hashing only fingerprints constraint *types*, not their
  // values, so swapping the `arrayContains` argument on the genre
  // pills wouldn't re-subscribe without it.
  const { data: meals, loading: mealsLoading } = useFirestoreCollection<CookbookMeal>(
    householdId ? "meal_catalog" : null,
    constraints,
    `cookbook:${selectedGenre}`,
  );

  const toggleDietary = (tag: DietaryTag) => {
    setActiveDietary((prev) => {
      const next = new Set(prev);
      if (next.has(tag)) next.delete(tag);
      else next.add(tag);
      return next;
    });
  };

  // Dietary filter is applied client-side (same as iOS) so we don't
  // need a per-tag index. ANY-match semantics match iOS
  // `dietaryFilter.isDisjoint`.
  const filteredMeals = useMemo(() => {
    if (activeDietary.size === 0) return meals;
    return meals.filter((m) => {
      const tags = new Set(m.dietary_tags ?? []);
      for (const t of activeDietary) {
        if (tags.has(t)) return true;
      }
      return false;
    });
  }, [meals, activeDietary]);

  const loading = ctxLoading || mealsLoading;

  const formatLabel = (raw: string) =>
    raw
      .replace(/_/g, " ")
      .replace(/\b\w/g, (c) => c.toUpperCase());

  return (
    <div className="p-4 space-y-4">
      <h1 className="text-2xl font-bold">{t("cookbook.title")}</h1>

      {/* Genre pill scroller — every iOS genre is shown; selecting
          one re-subscribes with the matching `arrayContains` filter.
          Mirrors iOS `CookbookView.swift:74` `ForEach(CookbookGenre
          .allCases)`. There is no "All" option on iOS — the
          Firestore query requires a single genre. */}
      <div className="flex gap-2 overflow-x-auto pb-1 -mx-4 px-4">
        {ALL_GENRES.map((g) => {
          const isActive = selectedGenre === g;
          return (
            <button
              key={g}
              type="button"
              onClick={() => setSelectedGenre(g)}
              className={`shrink-0 rounded-full px-3 py-1.5 text-sm font-medium whitespace-nowrap transition-colors ${
                isActive
                  ? "bg-[color:var(--accent-color)] text-white"
                  : "bg-gray-100 text-gray-700 dark:bg-white/10 dark:text-gray-300"
              }`}
            >
              {formatLabel(g)}
            </button>
          );
        })}
      </div>

      {/* Dietary chip row */}
      <div className="flex gap-2 overflow-x-auto pb-1 -mx-4 px-4">
        {DIETARY_TAGS.map((tag) => {
          const isActive = activeDietary.has(tag);
          return (
            <button
              key={tag}
              type="button"
              onClick={() => toggleDietary(tag)}
              className={`shrink-0 rounded-full px-3 py-1 text-xs font-medium whitespace-nowrap transition-colors border ${
                isActive
                  ? "bg-[color:var(--accent-color)] text-white border-[color:var(--accent-color)]"
                  : "bg-transparent text-gray-700 dark:text-gray-300 border-gray-300 dark:border-white/15"
              }`}
            >
              {formatLabel(tag)}
            </button>
          );
        })}
      </div>

      {/* Grid */}
      {loading ? (
        <div className="flex items-center justify-center h-40">
          <div
            className="w-8 h-8 border-3 border-t-transparent rounded-full animate-spin"
            style={{
              borderColor: "var(--accent-color, #00C7BE)",
              borderTopColor: "transparent",
            }}
          />
        </div>
      ) : filteredMeals.length === 0 ? (
        <Card>
          <EmptyState
            icon="\u{1F374}"
            title={t("cookbook.empty.title")}
            subtitle={t("cookbook.empty.subtitle")}
          />
        </Card>
      ) : (
        <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-5 gap-3">
          {filteredMeals.map((meal) => (
            <CookbookMealCard
              key={meal.uid ?? meal.name}
              meal={meal}
              onTap={(m) => setDetailMeal(m)}
            />
          ))}
        </div>
      )}

      {detailMeal && (
        <CookbookRecipeDetailSheet
          meal={detailMeal}
          onClose={() => setDetailMeal(null)}
        />
      )}
    </div>
  );
}
